// The annotations of the open image: shapes, selection, class, tool and undo stack.
// A plain object (not React state) so the canvas can draw from it directly and tests can drive it
// without a DOM. Mirrors the data and operations of AnnotationCanvas in frontend/ui/annotation_canvas.py.
import type { AnnotationOut } from "../api/annotations";
import type { LabelClass } from "../api/types";
import { MIN_BOX, normalized, rectH, rectW, type Pt, type Rect } from "./geometry";
import { UndoRedoManager, type Command } from "./history";
import {
  OBB_MIN_SIDE, cornersOf, insertVertex, isRectangular, obbEqual, obbFromCorners, obbRotateBy, removeVertex, rotatePolygonBy,
  type Obb,
} from "./shapes";

export type Mode = "select" | "bbox" | "polygon" | "obb";
export type Kind = "bbox" | "polygon";

export interface Ann {
  /** Stable id for this session; the server id is not known until the first save. */
  key: number;
  serverId: number | null;
  classId: number | null;
  kind: Kind;
  rect: Rect | null;
  /** A polygon's corners. For a rotated box they are always `cornersOf(obb)`. */
  points: Pt[] | null;
  /** Set when the polygon is a rotated box (a saved rectangle reopens as one, as on the desktop). Stored as four points. */
  obb: Obb | null;
  source: string;
  reviewed: boolean;
}

/** The position of a shape: what a move or resize changes and an undo restores. */
export interface Geom { rect: Rect | null; points: Pt[] | null; obb: Obb | null }

export const AUTO_COLOR = "#FF8C00";
export const DEFAULT_COLOR = "#4FC3F7";
/** Polygons and rotated boxes with no class are green, boxes blue (PolygonItem._update_style / BBoxItem). */
export const POLYGON_DEFAULT_COLOR = "#81C784";

const copyPts = (p: Pt[] | null) => p && p.map((q) => [q[0], q[1]] as Pt);
export const getGeom = (a: Ann): Geom => ({ rect: a.rect && { ...a.rect }, points: copyPts(a.points), obb: a.obb && { ...a.obb } });
export function setGeom(a: Ann, g: Geom) { a.rect = g.rect && { ...g.rect }; a.points = copyPts(g.points); a.obb = g.obb && { ...g.obb }; }
export function translated(g: Geom, dx: number, dy: number): Geom {
  return {
    rect: g.rect && { x1: g.rect.x1 + dx, y1: g.rect.y1 + dy, x2: g.rect.x2 + dx, y2: g.rect.y2 + dy },
    points: g.points && g.points.map(([x, y]) => [x + dx, y + dy] as Pt),
    obb: g.obb && { ...g.obb, cx: g.obb.cx + dx, cy: g.obb.cy + dy },
  };
}
/** A rotated box takes a new state; its four stored corners follow. */
export function setObb(a: Ann, o: Obb) { a.obb = { ...o }; a.points = cornersOf(o); }

/** The shape as the API stores it (BBoxItem.to_dict / PolygonItem.to_dict). */
export function coordinatesOf(a: Ann): unknown {
  return a.kind === "bbox" ? { x1: a.rect!.x1, y1: a.rect!.y1, x2: a.rect!.x2, y2: a.rect!.y2 } : { points: a.points!.map(([x, y]) => [x, y]) };
}

let nextKey = 1;

/** Shape data as the API stores it, to a shape; null when it cannot be shown (AnnotationCanvas._build_item_from_data). */
export interface ShapeData { id: number | null; shape_type: string; coordinates: unknown; class_id: number | null; source?: string; reviewed?: boolean }

export const newKey = () => nextKey++;

export function fromServer(a: ShapeData): Ann | null {
  const base = { key: nextKey++, serverId: a.id, classId: a.class_id, source: a.source || "manual", reviewed: !!a.reviewed };
  const c = a.coordinates as Record<string, unknown> | unknown[] | null;
  if (a.shape_type === "bbox" && c && !Array.isArray(c)) {
    const n = (k: string) => Number((c as Record<string, unknown>)[k] ?? 0);
    return { ...base, kind: "bbox", rect: { x1: n("x1"), y1: n("y1"), x2: n("x2"), y2: n("y2") }, points: null, obb: null };
  }
  if (a.shape_type === "polygon" && c) {
    const raw = Array.isArray(c) ? c : ((c as { points?: unknown[] }).points ?? []);
    const pts: Pt[] = [];
    for (const pair of raw as unknown[][]) {
      const x = Number(pair?.[0]), y = Number(pair?.[1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      pts.push([x, y]);
    }
    if (pts.length < 3) return null;
    // A saved shape that is already a rectangle reopens as an editable rotated box (is_rectangular is tight,
    // so a hand-traced quadrilateral stays a polygon with four independent corners).
    if (pts.length === 4 && isRectangular(pts)) {
      const obb = obbFromCorners(pts);
      return { ...base, kind: "polygon", rect: null, points: cornersOf(obb), obb };
    }
    return { ...base, kind: "polygon", rect: null, points: pts, obb: null };
  }
  return null;
}

export interface Counts { total: number; reviewed: number }

/** The copied shapes (AnnotationCanvas._clipboard): module-level, so it outlives the image and the page. */
let clipboard: { shape_type: string; coordinates: unknown; class_id: number | null }[] = [];
export const clipboardSize = () => clipboard.length;
export const clearClipboard = () => { clipboard = []; };

export class AnnotationDoc {
  anns: Ann[] = [];
  selection = new Set<number>();
  classes: LabelClass[] = [];
  currentClassId: number | null = null;
  /** The shape whose row the pointer is over in the side panel; the canvas outlines it. Not an edit. */
  hovered: number | null = null;
  mode: Mode = "select";
  dirty = false;
  /**
   * True from the moment an image is being swapped until its annotations have arrived. Nothing can be edited
   * then: a shape added in that window would be wiped by the load, or saved onto the wrong image.
   * (The desktop loads synchronously, so it has no such window.)
   */
  locked = true;
  /** A viewer: the server refuses every change, so the canvas does not offer to make one. */
  readOnly = false;
  /** Nothing can be edited: an image is loading, or the account is read-only. */
  get frozen(): boolean { return this.locked || this.readOnly; }
  /** Bumped on every change; what React subscribes to. */
  version = 0;
  /** Server annotations this version cannot show. They are never deleted by a save. */
  hidden = new Set<number>();
  readonly history = new UndoRedoManager();
  private listeners = new Set<() => void>();
  say: (msg: string) => void = () => {};

  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  getVersion = () => this.version;
  touch() { this.version++; for (const l of this.listeners) l(); }
  markDirty() { this.dirty = true; this.touch(); }
  setHovered(key: number | null) { if (this.hovered !== key) { this.hovered = key; this.touch(); } }

  // ── Loading ──────────────────────────────────────────────────────────────────────────────────
  load(server: AnnotationOut[]) {
    this.anns = []; this.selection.clear(); this.history.clear(); this.hidden.clear(); this.dirty = false; this.locked = false;
    for (const s of server) {
      const a = fromServer(s);
      if (a) this.anns.push(a); else this.hidden.add(s.id);
    }
    this.touch();
  }
  clear() { this.anns = []; this.selection.clear(); this.history.clear(); this.hidden.clear(); this.dirty = false; this.locked = true; this.touch(); }

  // ── Queries ──────────────────────────────────────────────────────────────────────────────────
  classOf(a: Ann): LabelClass | null { return this.classes.find((c) => c.id === a.classId) ?? null; }
  /** BBoxItem._update_style: unreviewed machine output is orange, otherwise the class colour. */
  colorOf(a: Ann): string {
    if (a.source === "auto" && !a.reviewed) return AUTO_COLOR;
    return this.classOf(a)?.color_hex ?? (a.kind === "bbox" ? DEFAULT_COLOR : POLYGON_DEFAULT_COLOR);
  }
  get currentClass(): LabelClass | null { return this.classes.find((c) => c.id === this.currentClassId) ?? null; }
  selected(): Ann[] { return this.anns.filter((a) => this.selection.has(a.key)); }
  counts(): Counts { return { total: this.anns.length, reviewed: this.anns.filter((a) => a.reviewed).length }; }

  // ── Mode and selection ───────────────────────────────────────────────────────────────────────
  setMode(m: Mode) { if (this.mode !== m) { this.mode = m; this.touch(); } }
  select(keys: Iterable<number>) { this.selection = new Set(keys); this.touch(); }
  toggle(key: number) { const s = new Set(this.selection); s.has(key) ? s.delete(key) : s.add(key); this.selection = s; this.touch(); }
  clearSelection() { if (this.selection.size) { this.selection = new Set(); this.touch(); } }

  selectAll() {
    if (!this.anns.length) { this.say("No annotations on this image to select"); return; }
    this.select(this.anns.map((a) => a.key));
    const n = this.anns.length;
    this.say(`Selected ${n} annotation${n !== 1 ? "s" : ""}`);
  }

  // ── Edits (each one is a command on the undo stack) ──────────────────────────────────────────
  /** A finished box drag; too small a box is discarded (BBoxTool.mouse_release). */
  addBox(rect: Rect): Ann | null {
    const r = normalized(rect);
    if (rectW(r) < MIN_BOX || rectH(r) < MIN_BOX) return null;
    const a: Ann = { key: nextKey++, serverId: null, classId: this.currentClassId, kind: "bbox", rect: r, points: null, obb: null, source: "manual", reviewed: false };
    this.history.execute(new AddCommand(this, a));
    this.markDirty();
    return a;
  }

  /** A finished polygon (PolygonTool.close_polygon): the current class, undoable as one add. */
  addPolygon(points: Pt[]): Ann {
    const a: Ann = { key: nextKey++, serverId: null, classId: this.currentClassId, kind: "polygon", rect: null, points: points.map((p) => [p[0], p[1]] as Pt), obb: null, source: "manual", reviewed: false };
    this.history.execute(new AddCommand(this, a));
    this.markDirty();
    return a;
  }

  /** A finished rotated-box drag (OBBTool.mouse_release): unrotated, and selected (the rest of the selection stays). */
  addObb(rect: Rect): Ann | null {
    const r = normalized(rect);
    if (rectW(r) < OBB_MIN_SIDE || rectH(r) < OBB_MIN_SIDE) return null;
    const obb: Obb = { cx: (r.x1 + r.x2) / 2, cy: (r.y1 + r.y2) / 2, w: rectW(r), h: rectH(r), angle: 0 };
    const a: Ann = { key: nextKey++, serverId: null, classId: this.currentClassId, kind: "polygon", rect: null, points: cornersOf(obb), obb, source: "manual", reviewed: false };
    this.history.execute(new AddCommand(this, a));
    this.selection = new Set([...this.selection, a.key]);
    this.markDirty();
    return a;
  }

  /** A polygon corner or rotation drag finished (ReshapePolygonCommand). */
  commitReshape(ann: Ann, old: Pt[], next: Pt[]) {
    this.history.execute(new ReshapeCommand(ann, old, next));
    this.markEdited(ann);
    this.markDirty();
  }
  commitTransform(ann: Ann, old: Obb, next: Obb) {
    if (obbEqual(old, next)) return;
    this.history.execute(new TransformObbCommand(ann, old, next));
    this.markEdited(ann);
    this.markDirty();
  }

  insertVertex(ann: Ann, index: number, p: Pt) { this.commitReshape(ann, ann.points!, insertVertex(ann.points!, index, p)); }
  /** False, and nothing changes, when the polygon is down to its last three corners. */
  removeVertex(ann: Ann, index: number): boolean {
    const next = removeVertex(ann.points!, index);
    if (!next) return false;
    this.commitReshape(ann, ann.points!, next);
    return true;
  }

  /** `[` `]` `{` `}` (AnnotationCanvas._nudge_rotation): each selected polygon or rotated box is its own undo step. */
  rotateSelected(degrees: number) {
    const shapes = this.selected().filter((a) => a.kind === "polygon");
    if (!shapes.length) { this.say("Select a polygon or rotated box first — [ and ] turn it 1°, Shift for 15°"); return; }
    for (const a of shapes) {
      if (a.obb) { const old = a.obb; const next = obbRotateBy(old, degrees); setObb(a, next); this.commitTransform(a, old, next); }
      else { const old = a.points!; const next = rotatePolygonBy(old, degrees); a.points = next; this.commitReshape(a, old, next); }
    }
    const n = shapes.length;
    this.say(`Rotated ${n} shape${n !== 1 ? "s" : ""} ${degrees < 0 ? "left" : "right"} ${Math.abs(degrees)}°`);
  }

  // ── Copy / paste / duplicate ─────────────────────────────────────────────────────────────────
  /** Ctrl+C: a snapshot of shape and class, kept for the whole session so it survives a change of image. */
  copySelected() {
    const sel = this.selected();
    if (!sel.length) { this.say("Nothing selected to copy — select an annotation first"); return; }
    clipboard = sel.map((a) => ({ shape_type: a.kind, coordinates: coordinatesOf(a), class_id: a.classId }));
    this.say(`Copied ${clipboard.length} annotation${clipboard.length !== 1 ? "s" : ""} — open another image and press Ctrl+V to paste`);
  }

  /**
   * Ctrl+V: the copied shapes, at the coordinates they were copied from, as new manual annotations. They become the
   * selection, ready to drag into place, and their class, when they share one, becomes the class new shapes get
   * (customer feedback, Oliver: copy a box, paste it, and carry on drawing that class). The desktop leaves both as
   * they were (docs/DESKTOP_PORT_BACKLOG.md #20).
   */
  paste(imageOpen: boolean) {
    if (!clipboard.length) { this.say("Clipboard is empty — copy an annotation first (Ctrl+C)"); return; }
    if (!imageOpen) return;
    const made: Ann[] = [];
    for (const c of clipboard) {
      const a = this.build({ id: null, shape_type: c.shape_type, coordinates: c.coordinates, class_id: c.class_id });
      if (!a) continue;
      this.history.execute(new AddCommand(this, a));
      made.push(a);
    }
    if (!made.length) { this.say("Nothing pasted — the clipboard held no valid shapes"); return; }
    this.selection = new Set(made.map((a) => a.key));
    const kinds = new Set(made.map((a) => a.classId));
    const [only] = kinds;
    if (kinds.size === 1 && only !== null) this.currentClassId = only;    // "No class" or a mix leaves the active class alone
    this.markDirty();
    this.say(`Pasted ${made.length} annotation${made.length !== 1 ? "s" : ""} — drag or use the arrow keys to place`);
  }

  /** Ctrl+D: a copy of each selected shape `offset` scene units down and right; the copies become the selection. */
  duplicateSelected(offset: number) {
    const sel = this.selected();
    if (!sel.length) { this.say("Nothing to duplicate — select a shape first"); return; }
    const made: Ann[] = [];
    for (const src of sel) {
      const a = this.build({ id: null, shape_type: src.kind, coordinates: coordinatesOf(src), class_id: src.classId });
      if (!a) continue;
      setGeom(a, translated(getGeom(a), offset, offset));
      this.history.execute(new AddCommand(this, a));
      made.push(a);
    }
    this.selection = new Set(made.map((a) => a.key));
    if (made.length) { this.markDirty(); this.say(`Duplicated ${made.length} shape${made.length !== 1 ? "s" : ""} — drag or use the arrow keys to place`); }
    else this.touch();
  }

  /** A new (unsaved) shape from API-shaped data; a class this project does not have becomes "no class". */
  private build(d: ShapeData): Ann | null {
    const a = fromServer({ ...d, source: "manual", reviewed: false });
    if (a && !this.classes.some((c) => c.id === a.classId)) a.classId = null;
    return a;
  }

  /** The context menu's "Delete annotation": this shape only, whatever else is selected. */
  deleteOne(a: Ann) {
    this.history.execute(new DeleteCommand(this, a));
    this.markDirty();
  }

  deleteSelected() {
    for (const a of this.selected()) this.history.execute(new DeleteCommand(this, a));
    this.markDirty();
  }

  /** Moves already shown live during a drag are recorded together as one undo step. */
  commitMoves(moves: { ann: Ann; old: Geom; next: Geom }[]) {
    const real = moves.filter((m) => JSON.stringify(m.old) !== JSON.stringify(m.next));
    if (!real.length) return;
    this.history.execute(new MoveCommand(real));
    for (const m of real) this.markEdited(m.ann);
    this.markDirty();
  }

  nudge(dx: number, dy: number) {
    const items = this.selected();
    if (!items.length) return;
    this.commitMoves(items.map((ann) => { const old = getGeom(ann); return { ann, old, next: translated(old, dx, dy) }; }));
  }

  commitResize(ann: Ann, old: Rect, next: Rect) {
    if (JSON.stringify(old) === JSON.stringify(next)) return;
    this.history.execute(new ResizeCommand(ann, old, next));
    this.markEdited(ann);
    this.markDirty();
  }

  /** A person touched an unreviewed machine annotation, so it counts as reviewed (_mark_edited). */
  markEdited(a: Ann) { if (a.source === "auto" && !a.reviewed) a.reviewed = true; }

  markSelectedReviewed() {
    let marked = 0, already = 0;
    for (const a of this.selected()) { if (a.reviewed) already++; else { a.reviewed = true; marked++; } }
    if (marked) { this.say(`Marked ${marked} annotation${marked !== 1 ? "s" : ""} as reviewed`); this.markDirty(); }
    else if (already) this.say("Selected annotation(s) were already marked reviewed");
    else this.say("Nothing to mark reviewed — select an annotation first");
  }

  applyClass(classId: number | null) {
    const sel = this.selected();
    if (!sel.length) { this.say("Nothing to relabel — select one or more annotations first"); return; }
    let changed = 0;
    for (const a of sel) {
      if (a.classId === classId) continue;
      this.history.execute(new RelabelCommand(a, a.classId, classId));
      changed++;
    }
    if (changed) {
      const name = this.classes.find((c) => c.id === classId)?.name ?? "(no class)";
      this.say(`Relabeled ${changed} annotation${changed !== 1 ? "s" : ""} as ${name}`);
      this.markDirty();
    } else this.say("Selected annotation(s) already have that class");
  }

  undo() { const d = this.history.undo(); if (d) { this.say(`Undo: ${d}`); this.prune(); this.markDirty(); } }
  redo() { const d = this.history.redo(); if (d) { this.say(`Redo: ${d}`); this.prune(); this.markDirty(); } }

  /** Drop selection entries for shapes that no longer exist. */
  prune() {
    const keys = new Set(this.anns.map((a) => a.key));
    this.selection = new Set([...this.selection].filter((k) => keys.has(k)));
  }
}

// ── Commands (undo_redo_manager.py) ────────────────────────────────────────────────────────────
class AddCommand implements Command {
  constructor(private doc: AnnotationDoc, private a: Ann) {}
  get description() { return `Add ${this.a.kind}`; }
  execute() { if (!this.doc.anns.includes(this.a)) this.doc.anns.push(this.a); }
  undo() { this.doc.anns = this.doc.anns.filter((x) => x !== this.a); this.doc.selection.delete(this.a.key); }
}

class DeleteCommand implements Command {
  constructor(private doc: AnnotationDoc, private a: Ann) {}
  get description() { return `Delete ${this.a.kind}`; }
  execute() { this.doc.anns = this.doc.anns.filter((x) => x !== this.a); this.doc.selection.delete(this.a.key); }
  undo() { this.doc.anns.push(this.a); }
}

class MoveCommand implements Command {
  constructor(private moves: { ann: Ann; old: Geom; next: Geom }[]) {}
  get description() { return this.moves.length === 1 ? "Move annotation" : `Move ${this.moves.length} annotations`; }
  execute() { for (const m of this.moves) setGeom(m.ann, m.next); }
  undo() { for (const m of this.moves) setGeom(m.ann, m.old); }
}

class RelabelCommand implements Command {
  constructor(private a: Ann, private old: number | null, private next: number | null) {}
  readonly description = "Relabel annotation";
  execute() { this.a.classId = this.next; }
  undo() { this.a.classId = this.old; }
}

class ResizeCommand implements Command {
  constructor(private a: Ann, private old: Rect, private next: Rect) {}
  readonly description = "Resize bounding box";
  execute() { this.a.rect = { ...this.next }; }
  undo() { this.a.rect = { ...this.old }; }
}

class ReshapeCommand implements Command {
  private old: Pt[]; private next: Pt[];
  constructor(private a: Ann, old: Pt[], next: Pt[]) { this.old = copyPts(old)!; this.next = copyPts(next)!; }
  readonly description = "Reshape polygon";
  execute() { this.a.points = copyPts(this.next); }
  undo() { this.a.points = copyPts(this.old); }
}

class TransformObbCommand implements Command {
  constructor(private a: Ann, private old: Obb, private next: Obb) {}
  readonly description = "Transform rotated box";
  execute() { setObb(this.a, this.next); }
  undo() { setObb(this.a, this.old); }
}
